// Inline "Enabled for: Ford, Boeing, Delta, +12 more" line shown on
// feature cards + Timeline announcement cards. Externals-only by
// default (per persona choice); falls back to a raw count if there are
// no external workspaces or the workspaces list hasn't classified any
// of them yet (so cards never go blank).
import { useMemo } from "react";
import type { FeatureFlag } from "@release-hub/shared";
import { useWorkspaces } from "@/api/queries";
import {
  effectiveWorkspaces,
  groupWorkspacesByClassification,
} from "@/lib/featureWorkspaces";

const PREVIEW = 3;

export function EnabledWorkspacesLine({ flags }: { flags: FeatureFlag[] }) {
  const { data: workspaces } = useWorkspaces();

  const state = useMemo(() => {
    if (!workspaces) return null;
    const eff = effectiveWorkspaces(flags, workspaces);
    if (eff.isEveryone) return { kind: "everyone" as const };
    if (eff.ids.length === 0) return null;
    const wsById = new Map(workspaces.map((w) => [w.id, w]));
    const enabled = eff.ids
      .map((id) => wsById.get(id))
      .filter((w): w is NonNullable<typeof w> => !!w);
    const grouped = groupWorkspacesByClassification(enabled);
    return { kind: "some" as const, total: enabled.length, grouped };
  }, [flags, workspaces]);

  if (!state) return null;
  if (state.kind === "everyone") {
    return (
      <p className="mt-2 text-xs text-text-secondary">
        <span className="font-semibold uppercase tracking-wider text-ga">
          Enabled for:
        </span>{" "}
        All workspaces
      </p>
    );
  }

  const externals = state.grouped.external;
  // Fallback: no classifications loaded yet (or none are `revenue`) —
  // don't leave the card blank; just show the count. Once /workspaces
  // returns real classifications, this branch becomes rare.
  const displayNames = (
    externals.length > 0 ? externals : state.grouped.unclassified
  )
    .map((w) => w.name)
    .sort((a, b) => a.localeCompare(b));

  if (displayNames.length === 0) {
    // Every enabled workspace is internal/partner. Rare in practice.
    return (
      <p
        className="mt-2 text-xs text-text-secondary"
        title="All enabled workspaces are internal or partner accounts."
      >
        <span className="font-semibold uppercase tracking-wider text-text-tertiary">
          Enabled for:
        </span>{" "}
        {state.total} internal
      </p>
    );
  }

  const preview = displayNames.slice(0, PREVIEW);
  const overflow = displayNames.length - preview.length;

  return (
    <p
      className="mt-2 truncate text-xs text-text-secondary"
      title={displayNames.join(" · ")}
    >
      <span className="font-semibold uppercase tracking-wider text-ga">
        Enabled for:
      </span>{" "}
      {preview.join(", ")}
      {overflow > 0 && (
        <span className="ml-1 text-text-tertiary">+{overflow} more</span>
      )}
    </p>
  );
}
